import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { AdditiveBlending, Box3, Color, Quaternion, Vector3, type Group, type Sprite, type Texture } from 'three';
import type { Building, RegionTree } from '@map/shared';
import type { Territory } from '@map/worldkit';
import { isTouch } from '../lib/useIsMobile.js';
import { prefersReducedMotion, sceneColor } from '../styles/tokens.js';
import { glowTexture } from './planetSkin.js';

/**
 * The people from Ilm (Faith › Seeking Knowledge › People), each a figure on
 * their own plot: the scholars, narrators and Companions met in lessons.
 *
 * The Prophet ﷺ is not a figure. He is drawn as an orb of light over his
 * plot, as the one the rest of them took from.
 *
 * Clicking works as it does for the camels: each one is projected to the
 * screen and the nearest within its own size is opened, so it is the same
 * gesture for both and a drag never opens anything.
 */
export interface PeopleProps {
  territories: Map<string, Territory>;
  tree: RegionTree;
  buildings: Building[];
  selectedId: string | null;
  onOpen?: (regionId: string, buildingId: string) => void;
}

const MODEL = '/models/warrior.glb';
/** Land is drawn at 1.02; a figure stands a hair above it. */
const GROUND = 1.0205;
const REACH_MIN = 14;
const REACH_MAX = 70;

interface Figure {
  id: string;
  regionId: string;
  prophet: boolean;
  /** Where its plot is, on the unit sphere. */
  up: Vector3;
  /** Standing height, in world units. */
  height: number;
  heading: number;
}

function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967295;
}

export function People({ territories, tree, buildings, selectedId, onOpen }: PeopleProps) {
  const figures = useMemo(() => {
    const out: Figure[] = [];
    for (const building of buildings) {
      if (building.kind !== 'person' || !building.person) continue;
      const territory = territories.get(building.regionId);
      if (!territory || !tree.regions[building.regionId]) continue;
      out.push({
        id: building.id,
        regionId: building.regionId,
        prophet: building.person.prophet,
        up: new Vector3(territory.site.x, territory.site.y, territory.site.z).normalize(),
        // Sized to the plot like the camels, a little taller since it stands upright.
        // People share the continent with the faculties now, so plots are about
        // half the size they were; the factor keeps each figure as tall as before.
        height: Math.min(0.06, Math.max(0.014, Math.sqrt(territory.area) * 0.1)),
        heading: hash(building.id) * Math.PI * 2,
      });
    }
    return out;
  }, [buildings, territories, tree]);

  // The world turns slowly when nothing is selected, so picking has to see
  // the figures where the turn has put them, not where they were laid down.
  const root = useRef<Group>(null);
  usePicking(figures, root, onOpen);

  const glow = useMemo(() => glowTexture(), []);
  useEffect(() => () => glow?.dispose(), [glow]);

  if (figures.length === 0) return null;
  return (
    <group ref={root}>
      {figures.map((figure) =>
        figure.prophet ? (
          <Light key={figure.id} figure={figure} glow={glow} highlighted={figure.regionId === selectedId} />
        ) : (
          <Suspense key={figure.id} fallback={null}>
            <Warrior figure={figure} highlighted={figure.regionId === selectedId} />
          </Suspense>
        ),
      )}
    </group>
  );
}

/** Stands a group on the sphere at `up`, turned `heading` about its own up. */
function useStance(up: Vector3, heading: number) {
  return useMemo(() => {
    const quaternion = new Quaternion()
      .setFromUnitVectors(new Vector3(0, 1, 0), up)
      .multiply(new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), heading));
    return { position: up.clone().multiplyScalar(GROUND), quaternion };
  }, [up, heading]);
}

function Warrior({ figure, highlighted }: { figure: Figure; highlighted: boolean }) {
  const { position, quaternion } = useStance(figure.up, figure.heading);
  const { scene } = useGLTF(MODEL);
  // Geometry and materials are shared; only the transform is the figure's own.
  const object = useMemo(() => {
    const copy = scene.clone(true);
    const size = new Box3().setFromObject(copy).getSize(new Vector3());
    copy.scale.setScalar(figure.height / Math.max(size.y, 1e-6));
    return copy;
  }, [scene, figure.height]);

  return (
    <group position={position} quaternion={quaternion}>
      <primitive object={object} />
      {highlighted && <Ring width={figure.height * 0.45} />}
    </group>
  );
}

/** The Prophet ﷺ: a bright core in a slow-breathing golden halo, floating over his plot. */
function Light({ figure, glow, highlighted }: { figure: Figure; glow: Texture | null; highlighted: boolean }) {
  const { position, quaternion } = useStance(figure.up, 0);
  const core = useMemo(() => new Color(sceneColor('prophet-light', '#fffaf0')), []);
  const halo = useMemo(() => new Color(sceneColor('prophet-halo', '#f3d77e')), []);
  const inner = useRef<Sprite>(null);
  const outer = useRef<Sprite>(null);
  const { invalidate } = useThree();
  const still = prefersReducedMotion();
  const r = figure.height * 0.3;
  const lift = figure.height * 0.75;

  useFrame((state) => {
    const breath = still ? 0.5 : 0.5 + Math.sin(state.clock.elapsedTime * 1.3) * 0.5;
    if (inner.current) inner.current.scale.setScalar(r * (4 + breath * 0.8));
    if (outer.current) {
      outer.current.scale.setScalar(r * (9 + breath * 3));
      (outer.current.material as { opacity: number }).opacity = 0.35 + breath * 0.25;
    }
    if (!still) invalidate();
  });

  return (
    <group position={position} quaternion={quaternion}>
      <mesh position={[0, lift, 0]}>
        <sphereGeometry args={[r, 24, 16]} />
        <meshBasicMaterial color={core} toneMapped={false} />
      </mesh>
      {glow && (
        <>
          <sprite ref={inner} position={[0, lift, 0]}>
            <spriteMaterial map={glow} color={core} blending={AdditiveBlending} depthWrite={false} transparent toneMapped={false} />
          </sprite>
          <sprite ref={outer} position={[0, lift, 0]}>
            <spriteMaterial map={glow} color={halo} blending={AdditiveBlending} depthWrite={false} transparent toneMapped={false} />
          </sprite>
        </>
      )}
      {highlighted && <Ring width={figure.height * 0.45} />}
    </group>
  );
}

function Ring({ width }: { width: number }) {
  return (
    <mesh position={[0, 0.0005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[width * 0.8, width, 32]} />
      <meshBasicMaterial color="#ffffff" transparent opacity={0.5} />
    </mesh>
  );
}

/** A click (not a drag) near a figure opens it, judged on screen like the camels. */
function usePicking(figures: Figure[], root: React.RefObject<Group | null>, onOpen: PeopleProps['onOpen']) {
  const { camera, gl } = useThree();
  const positions = useMemo(
    () => figures.map((f) => f.up.clone().multiplyScalar(GROUND + f.height * 0.5)),
    [figures],
  );
  useEffect(() => {
    if (!onOpen || figures.length === 0) return;
    const canvas = gl.domElement;
    const screen = new Vector3();
    const edge = new Vector3();
    const toCamera = new Vector3();
    const pick = (event: PointerEvent, generosity: number): Figure | null => {
      const rect = canvas.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      let best: Figure | null = null;
      let bestDistance = Infinity;
      const world = new Vector3();
      positions.forEach((local, index) => {
        const figure = figures[index];
        if (!figure) return;
        const position = root.current ? world.copy(local).applyMatrix4(root.current.matrixWorld) : local;
        toCamera.copy(camera.position).sub(position);
        if (toCamera.dot(position) <= 0) return;
        screen.copy(position).project(camera);
        const sx = ((screen.x + 1) / 2) * rect.width;
        const sy = ((1 - screen.y) / 2) * rect.height;
        edge.copy(position).addScaledVector(position, figure.height / position.length()).project(camera);
        const ex = ((edge.x + 1) / 2) * rect.width;
        const ey = ((1 - edge.y) / 2) * rect.height;
        const reach = Math.min(REACH_MAX, Math.max(REACH_MIN, Math.hypot(ex - sx, ey - sy) * 1.6)) * generosity;
        const d = Math.hypot(sx - px, sy - py);
        if (d < reach && d < bestDistance) {
          bestDistance = d;
          best = figure;
        }
      });
      return best;
    };

    let from: { x: number; y: number; id: number } | null = null;
    const onDown = (event: PointerEvent) => {
      from = { x: event.clientX, y: event.clientY, id: event.pointerId };
    };
    const onUp = (event: PointerEvent) => {
      const began = from;
      from = null;
      if (!began || began.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - began.x, event.clientY - began.y) > 8) return;
      const figure = pick(event, isTouch() ? 2.4 : 1.6);
      if (figure) onOpen(figure.regionId, figure.id);
    };
    const onCancel = () => (from = null);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
    };
  }, [figures, positions, root, camera, gl, onOpen]);
}

useGLTF.preload(MODEL);
